import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ScrollText, Search, Shield, Globe, BookOpen, Download, 
  Eye, ChevronDown, ChevronRight, Map, Flag, Scale,
  FileText, MessageCircle, Play, User, Crown, Sparkles, Loader2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { rightsReportAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import AuthModal from '../components/AuthModal';
import SubscriptionGate from '../components/SubscriptionGate';
import useSubscription from '../hooks/useSubscription';
import TreatyLibrary from '../components/TreatyLibrary';
import RightsToolbox from '../components/RightsToolbox';
import TribalRightsGuru from '../components/TribalRightsGuru';
import RitesAndCeremonies from '../components/RitesAndCeremonies';

const TribalVault = () => {
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const { isPaidUser, hasFeatureAccess, getUpgradeMessage } = useSubscription();
  const [activeSection, setActiveSection] = useState('treaties');
  const [userRightsData, setUserRightsData] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Check if user is admin (same logic as Header.jsx)
  const isAdmin = user && (
    (user.email && user.email.toLowerCase().includes('admin')) || 
    (user.role && user.role.toLowerCase() === 'admin') ||
    (user.is_admin === true)
  );

  // Allow access ONLY for admin users or paid subscribers (Seekers or Crown)
  const hasVaultAccess = isAdmin || isPaidUser;

  useEffect(() => {
    if (isAuthenticated && user) {
      // Mock user rights data - in real app, this would come from genealogy analysis
      setUserRightsData({
        tribalRegion: 'Southeastern United States',
        ancestralFlags: ['Cherokee Nation', 'Seminole'],
        treatyRights: [
          'Treaty of New Echota (1835) - Land and hunting rights',
          'Treaty of Moultrie Creek (1823) - Seminole territorial rights'
        ],
        landAgreements: [
          'Traditional hunting grounds in Georgia and Florida',
          'Sacred site protections under NAGPRA'
        ],
        exemptions: [
          'State hunting license exemptions on traditional lands',
          'Certain federal tax exemptions for tribal income'
        ]
      });
    }
  }, [isAuthenticated, user]);

  const handleGenerateReport = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to generate your rights report.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsGeneratingReport(true);
      
      toast({
        title: "Generating Report",
        description: "Creating your comprehensive rights report... This may take a moment.",
      });

      // Generate the report
      const reportResponse = await rightsReportAPI.generateReport({
        include_detailed_treaties: true,
        include_cultural_heritage: true,
        include_legal_guidance: true,
        format: 'pdf'
      });

      // Download the report
      const reportBlob = await rightsReportAPI.downloadReport(reportResponse.report_id);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([reportBlob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tribal_rights_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Report Generated Successfully",
        description: "Your comprehensive rights report has been downloaded. Check your downloads folder.",
        variant: "default"
      });

    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: "Report Generation Failed",
        description: "There was an error generating your rights report. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Keep track of authentication for advanced features
  const showAdvancedFeatures = isAuthenticated;
  
  // Require authentication AND paid subscription for access
  if (!hasVaultAccess) {
    return (
      <SubscriptionGate 
        feature="tribal_vault_access"
        title="Unlock the Tribal Vault"
        description="Access comprehensive treaty databases, understand your ancestral rights, and learn how to assert the protections your ancestors negotiated. Upgrade to Seekers Path or Crown membership to unlock full access."
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-green-50 to-blue-50">
      {/* Hero Banner */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        {/* Background with treaty parchment effect */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-100/80 via-green-100/60 to-blue-100/80"></div>
          <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23654321%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%227%22%20cy%3D%227%22%20r%3D%222%22/%3E%3Ccircle%20cx%3D%2227%22%20cy%3D%227%22%20r%3D%222%22/%3E%3Ccircle%20cx%3D%2247%22%20cy%3D%227%22%20r%3D%222%22/%3E%3Ccircle%20cx%3D%227%22%20cy%3D%2227%22%20r%3D%222%22/%3E%3Ccircle%20cx%3D%2227%22%20cy%3D%2227%22%20r%3D%222%22/%3E%3Ccircle%20cx%3D%2247%22%20cy%3D%2227%22%20r%3D%222%22/%3E%3Ccircle%20cx%3D%227%22%20cy%3D%2247%22%20r%3D%222%22/%3E%3Ccircle%20cx%3D%2227%22%20cy%3D%2247%22%20r%3D%222%22/%3E%3Ccircle%20cx%3D%2247%22%20cy%3D%2247%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
          {/* Glowing data nodes effect */}
          <div className="absolute top-20 left-20 w-3 h-3 bg-green-400 rounded-full animate-pulse shadow-lg"></div>
          <div className="absolute top-32 right-32 w-2 h-2 bg-blue-400 rounded-full animate-pulse shadow-lg"></div>
          <div className="absolute bottom-40 left-1/3 w-4 h-4 bg-amber-400 rounded-full animate-pulse shadow-lg"></div>
          <div className="absolute bottom-20 right-20 w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-lg"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Know Your Rights.
              <br />
              <span className="text-green-700">Reclaim Your Treaty.</span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed mb-8">
              Access comprehensive treaty databases, understand your ancestral rights, and learn how to assert 
              the protections your ancestors negotiated centuries ago.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                size="lg" 
                className="bg-green-700 hover:bg-green-800 text-white px-8 py-4 text-lg font-semibold transition-all hover:scale-105 shadow-lg"
                onClick={() => {
                  setActiveSection('treaties');
                  // Scroll to treaties section after a short delay to allow tab change
                  setTimeout(() => {
                    const treatiesSection = document.querySelector('[data-section="treaties"]');
                    if (treatiesSection) {
                      treatiesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
              >
                <Map className="w-5 h-5 mr-2" />
                Browse Treaties by Region
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 text-lg font-semibold transition-all hover:scale-105"
                onClick={() => {
                  // Scroll to Personal Rights Summary section
                  const personalRightsSection = document.querySelector('[data-section="personal-rights"]');
                  if (personalRightsSection) {
                    personalRightsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                <Search className="w-5 h-5 mr-2" />
                Run AI Rights Discovery
              </Button>
            </div>
          </div>

          {/* User Personal Rights Summary */}
          {isAuthenticated && userRightsData && (
            <Card className="max-w-4xl mx-auto bg-white/90 backdrop-blur-sm shadow-xl border-0" data-section="personal-rights">
              <CardHeader className="bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center text-xl">
                  <Crown className="w-6 h-6 mr-3" />
                  Your Personal Rights Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Map className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900">Tribal Region</h3>
                    <p className="text-gray-600">{userRightsData.tribalRegion}</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Flag className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900">Ancestral Flags</h3>
                    <div className="flex flex-wrap gap-1 justify-center">
                      {userRightsData.ancestralFlags.map((flag, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {flag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Scale className="w-8 h-8 text-amber-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900">Active Rights</h3>
                    <p className="text-sm text-gray-600">{userRightsData.treatyRights.length + userRightsData.exemptions.length} Applicable</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <Shield className="w-4 h-4 mr-2 text-green-600" />
                      Treaty Rights
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {userRightsData.treatyRights.map((right, index) => (
                        <li key={index} className="flex items-start">
                          <ChevronRight className="w-3 h-3 mr-1 mt-1 text-green-500 flex-shrink-0" />
                          {right}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <FileText className="w-4 h-4 mr-2 text-blue-600" />
                      Land Agreements
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {userRightsData.landAgreements.map((agreement, index) => (
                        <li key={index} className="flex items-start">
                          <ChevronRight className="w-3 h-3 mr-1 mt-1 text-blue-500 flex-shrink-0" />
                          {agreement}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <ScrollText className="w-4 h-4 mr-2 text-amber-600" />
                      Travel & Tax Exemptions
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {userRightsData.exemptions.map((exemption, index) => (
                        <li key={index} className="flex items-start">
                          <ChevronRight className="w-3 h-3 mr-1 mt-1 text-amber-500 flex-shrink-0" />
                          {exemption}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="text-center">
                  <Button 
                    className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-6 py-2 disabled:opacity-50"
                    onClick={handleGenerateReport}
                    disabled={isGeneratingReport}
                  >
                    {isGeneratingReport ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating Report...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Generate My Rights Report
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Login prompt for non-authenticated users */}
          {!isAuthenticated && (
            <Card className="max-w-2xl mx-auto bg-white/90 backdrop-blur-sm shadow-lg">
              <CardContent className="p-6 text-center">
                <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Discover Your Personal Treaty Rights
                </h3>
                <p className="text-gray-600 mb-4">
                  Sign in to access your personalized rights summary based on your ancestral lineage analysis.
                </p>
                <AuthModal>
                  <Button className="bg-green-700 hover:bg-green-800 text-white px-6 py-2">
                    Sign In to Access Personal Rights
                  </Button>
                </AuthModal>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Main Content Sections */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-1 h-auto p-1 mb-4 sm:mb-8 sm:grid sm:grid-cols-4">
              <TabsTrigger value="treaties" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2 whitespace-nowrap">
                <ScrollText className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Treaties
              </TabsTrigger>
              <TabsTrigger value="ceremonies" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2 whitespace-nowrap">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Ceremonies
              </TabsTrigger>
              <TabsTrigger value="toolbox" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2 whitespace-nowrap">
                <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Toolbox
              </TabsTrigger>
              <TabsTrigger value="guru" className="text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2 whitespace-nowrap">
                <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Ask Guru
              </TabsTrigger>
            </TabsList>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Main Content Area */}
              <div className="lg:col-span-3">
                <TabsContent value="treaties" data-section="treaties">
                  <TreatyLibrary />
                </TabsContent>
                
                <TabsContent value="ceremonies">
                  <RitesAndCeremonies />
                </TabsContent>
                
                <TabsContent value="toolbox">
                  <RightsToolbox />
                </TabsContent>
                
                <TabsContent value="guru">
                  <TribalRightsGuru />
                </TabsContent>
              </div>

              {/* Sidebar - Always visible */}
              <div className="lg:col-span-1">
                <div className="sticky top-4 space-y-4">
                  {/* Quick Actions Card */}
                  <Card className="bg-white shadow-lg">
                    <CardHeader>
                      <CardTitle className="text-lg">Quick Actions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Button 
                        variant="outline" 
                        className="w-full justify-start"
                        onClick={() => {
                          setActiveSection('treaties');
                          // Scroll to treaties section after a short delay to allow tab change
                          setTimeout(() => {
                            const treatiesSection = document.querySelector('[data-section="treaties"]');
                            if (treatiesSection) {
                              treatiesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }, 100);
                        }}
                      >
                        <Map className="w-4 h-4 mr-2" />
                        Browse Treaties
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start"
                        onClick={() => {
                          // Scroll to Personal Rights Summary section
                          const personalRightsSection = document.querySelector('[data-section="personal-rights"]');
                          if (personalRightsSection) {
                            personalRightsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }}
                      >
                        <Search className="w-4 h-4 mr-2" />
                        AI Rights Discovery
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start"
                        onClick={() => setActiveSection('toolbox')}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Templates
                      </Button>
                    </CardContent>
                  </Card>


                </div>
              </div>
            </div>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default TribalVault;